import { createSelector } from 'reselect';
import { FrameRange } from 'content-creation-common/models';

import {
  inEpisodeFlow,
  inSimpleFlow,
} from '../../routes/state-management/selectors';
import { getRootFrameRange } from '../../shared/playback/state-management/selectors';

import { isNil } from '../../../util/object';

import { getSelectedDisplayElementId } from '../../editor/shared/state-management/selectors';
import {
  getAdjacentSkippedShotContainerLayers,
  getNonSkippedShotContainerLayers,
  getSkippedShotContainerLayers,
} from '../../video-reformatting/state-management/selectors';
import { getAllElementsById } from '../../../selectors/element';
import { isEmpty as isEmptyArray } from '../../../util/array';
import { getMaxVideoPublicationDurationInSeconds } from '../../shared/creative/state-management/selectors';
import { PLAYBACK_FRAMES_PER_SECOND } from '../../../constants';

/**
 * @function * getPlayableFrameRanges
 * @return {FrameRange[]}
 */
export const getPlayableFrameRanges = createSelector(
  [
    inSimpleFlow,
    inEpisodeFlow,
    getRootFrameRange,
    getSelectedDisplayElementId,
    getAllElementsById,
    getNonSkippedShotContainerLayers,
    getAdjacentSkippedShotContainerLayers,
  ],
  (
    isInSimpleFlow,
    isInEpisodeFlow,
    rootFrameRange,
    selectedElementId,
    allElementsById,
    nonSkippedShotContainerLayers,
    consecutiveSkippedLayers,
  ) => {
    if (isInSimpleFlow) {
      // if play is specified to play in the range of element's start and end
      if (isNil(selectedElementId)) return [rootFrameRange];

      const element = allElementsById[selectedElementId];
      if (isNil(element)) return [rootFrameRange];

      const startFrame = Math.max(element.frameStart, rootFrameRange.start);
      const endFrame = Math.min(element.frameEnd, rootFrameRange.end);
      return [new FrameRange(startFrame, endFrame)];
    }

    if (isInEpisodeFlow) {
      return FrameRange.union(
        [...nonSkippedShotContainerLayers, ...consecutiveSkippedLayers].map(
          (layer) => {
            const startFrame = Math.max(layer.startFrame, rootFrameRange.start);
            const endFrame = Math.min(layer.endFrame, rootFrameRange.end);
            return new FrameRange(startFrame, endFrame);
          },
        ),
      );
    }

    return [rootFrameRange];
  },
);

export const getPlayableFrameNumbers = createSelector(
  [getPlayableFrameRanges],
  (playableFrameRanges) => {
    if (isEmptyArray(playableFrameRanges)) return [];
    return playableFrameRanges.reduce((frameNumbers, playableFrameRange) => {
      for (let i = 0; i < playableFrameRange.length; i++) {
        frameNumbers.push(playableFrameRange.start + i);
      }
      return frameNumbers;
    }, []);
  },
);

export const getSkippedFrameNumbers = createSelector(
  [getSkippedShotContainerLayers],
  (skippedShotLayers) => {
    if (isEmptyArray(skippedShotLayers)) return [];
    return skippedShotLayers.reduce((frameNumbers, shotLayer) => {
      const { endFrame, startFrame } = shotLayer;

      for (let i = startFrame; i <= endFrame; i++) {
        frameNumbers.push(i);
      }

      return frameNumbers;
    }, []);
  },
);

/**
 * @function getMaxFramesForVideoPublication
 * @param {Object} state
 * @return {Number} Maximum number of frames that can be in a published video
 */
const getMaxFramesForVideoPublication = createSelector(
  [getMaxVideoPublicationDurationInSeconds],
  (maxVideoPublicationInSeconds) =>
    maxVideoPublicationInSeconds * PLAYBACK_FRAMES_PER_SECOND,
);

/**
 * @function getIsVideoPublicationLargerThanMax
 * @param {Object} state
 * @return {Boolean}
 */
export const getIsVideoPublicationLargerThanMax = createSelector(
  [getMaxFramesForVideoPublication, getPlayableFrameNumbers, inEpisodeFlow],
  (maxFramesForVideoPublication, playableFrameNumbers, isInEpisodeFlow) =>
    isInEpisodeFlow &&
    playableFrameNumbers.length > maxFramesForVideoPublication,
);
